import type {
  DataTableColumn,
  DataTableInstance,
  DataTableRowData,
} from 'blocks/cx-data-table/types'
import clsx from 'clsx'
import { debounce } from 'lodash'
import { SearchInput } from 'primitives/input'
import { type ChangeEvent, useCallback, useMemo, useState } from 'react'
import { DataTableShowHideColumnsMenuItems } from './column-visibility-menu-item'

interface Props<TData extends DataTableRowData> {
  table: DataTableInstance<TData>
}

export const ShowHideColumnsMenu = <TData extends DataTableRowData>({ table }: Props<TData>) => {
  const { getAllColumns, getCenterLeafColumns, getLeftLeafColumns, getRightLeafColumns, getState } =
    table
  const { columnOrder, columnPinning } = getState()

  const allColumns = useMemo(() => {
    const columns = getAllColumns()
    if (columnOrder.length > 0 && !columns.some((col) => col.columnDef.columnDefType === 'group')) {
      return [
        ...getLeftLeafColumns(),
        ...Array.from(new Set(columnOrder)).map((colId) =>
          getCenterLeafColumns().find((col) => col?.id === colId),
        ),
        ...getRightLeafColumns(),
      ].filter(Boolean)
    }
    return columns
  }, [
    columnOrder,
    columnPinning,
    getAllColumns(),
    getCenterLeafColumns(),
    getLeftLeafColumns(),
    getRightLeafColumns(),
  ]) as DataTableColumn<TData>[]

  const [hoveredColumn, setHoveredColumn] = useState<DataTableColumn<TData> | null>(null)
  const [columnSearchParam, setColumnSearchParam] = useState<string>('')

  const handleSearchChange = useCallback(
    debounce((event: ChangeEvent<HTMLInputElement>) => {
      setColumnSearchParam(event.target.value ?? '')
    }, 250),
    [],
  )

  return (
    <div
      className={clsx(
        'mrt-show-hide-columns-menu',
        'overflow-y-auto flex flex-col bg-background p-0 max-h-[calc(80vh-100px)]',
      )}
    >
      <div className="sticky top-0 bg-background z-10 p-1">
        <SearchInput
          placeholder="Search"
          variant="ghost"
          className="w-full border-b-1 font-inter text-s-400 normal-case tracking-normal"
          onChange={handleSearchChange}
        />
      </div>

      {allColumns
        .filter(
          (column) =>
            typeof column.columnDef.header === 'string' &&
            column.columnDef.header
              .toLocaleLowerCase()
              .includes(columnSearchParam?.toLocaleLowerCase()),
        )
        .map((column, index) => (
          <DataTableShowHideColumnsMenuItems
            allColumns={allColumns}
            column={column}
            hoveredColumn={hoveredColumn}
            key={`${index}-${column.id}`}
            setHoveredColumn={setHoveredColumn}
            table={table}
          />
        ))}
    </div>
  )
}
